import React, { useState, useEffect, useRef } from 'react';
import { redux } from 'zustand/middleware';
import usePersistStore from '../stores/persistStore';
import {
    Box,
    Flex,
    Icon,
    IconButton,
    Tooltip,
    useColorMode,
    Text
} from '@chakra-ui/react';
import { EditPenIcon, EditPenIconFFF, RefreshIcon } from './UiElements';
import { tooltipAnimation } from './customTheme';

export const formatLocalStorageValue = (value, isUsingMac) => {
    let formattedValue = value.toUpperCase();
    if (isUsingMac) {
        formattedValue = formattedValue.replace('META', '⌘');
        formattedValue = formattedValue.replace('ALT', '⌥');
    }
    formattedValue = formattedValue.replace('SHIFT', '⇧');
    formattedValue = formattedValue.replace('ALT', 'Alt');
    formattedValue = formattedValue.replace('CMD', 'Cmd');
    formattedValue = formattedValue.replace('META', 'Meta');
    formattedValue = formattedValue.replace('Meta', '⌘');

    formattedValue = formattedValue.replace('CTRL', 'Ctrl');
    formattedValue = formattedValue.replaceAll('+', ' + ');
    return formattedValue;
};

function useOutsideAlerter(ref) {
    useEffect(() => {
        /**
         * Alert if clicked on outside of element
         */
        function handleClickOutside(event) {
            if (ref.current && !ref.current.contains(event.target)) {
                alert('You clicked outside of me2!');
            }
        }
        // Bind the event listener
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [ref]);
}

export function OutsideAlerter(props) {
    const wrapperRef = useRef(null);
    useOutsideAlerter(wrapperRef);

    return <div ref={wrapperRef}>{props.children}</div>;
}

const ShortcutRecorder = ({
    type,
    defaultValue,
    setIndexSel,
    indexSel,
    index,
    isUsingMac
}: any) => {
    const localStorageValue = formatLocalStorageValue(
        usePersistStore((state) => state[type]),
        isUsingMac
    );
    const setLocalStorage = (newVal) =>
        usePersistStore.setState({ [type]: newVal });
    const [collapsed, setCollapsed] = useState(true);
    const [invalidText, setInvalidText] = useState('');
    const [inputValue, setInputValue] = useState(localStorageValue);
    const { colorMode, toggleColorMode } = useColorMode();
    const iconSmall = { light: 'gray.700', dark: '#FFF' };
    const bgColor = { light: 'gray.100', dark: '#3F454D' };
    const subTitle = { light: 'gray.500', dark: 'gray.400' };
    const text = { light: '#1A202C', dark: '#FFF' };
    const icon = { light: 'gray.500', dark: '#A1A2A4' };
    const iconColor = { light: 'gray.600', dark: 'gray.400' };
    const hoverText = { light: 'gray.700', dark: 'gray.300' };

    useEffect(() => {
        if (collapsed) {
            setIndexSel(null);
            return;
        }
        setIndexSel(index);
        window.addEventListener('keyup', upHandler);
        window.addEventListener('keydown', downHandler);
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            window.removeEventListener('keydown', downHandler);
            window.removeEventListener('keyup', upHandler);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [collapsed]);

    const [shiftHeld, setShiftHeld] = useState(false);
    const [altHeld, setAltHeld] = useState(false);
    const [controlHeld, setControlHeld] = useState(false);
    const [commandHeld, setCommandHeld] = useState(false);
    const [letterHeld, setLetterHeld] = useState(null);
    const searchInput = useRef(null);
    const boxRef = useRef(null);

    const recordShortcut = () => {
        setInputValue('');
        setLocalStorage('');
        setCollapsed(false);
        setInvalidText('');
        //searchInput.current.focus();
    };

    const turnOffKeyRecording = () => {
        setCollapsed(true);
        setShiftHeld(false);
        setAltHeld(false);
        setCommandHeld(false);
        setControlHeld(false);
        setLetterHeld(null);
        setInvalidText('');
        //searchInput.current.blur();
    };

    function handleClickOutside(event) {
        if (boxRef.current && !boxRef.current.contains(event.target)) {
            setCollapsed(true);
        }
    }

    const isLetter = (str) => {
        return str.length === 1 && str.match(/[a-z]/i);
    };

    let invalidTextDefault = 'Include Shift, alt, ctrl or cmd';
    if (isUsingMac) {
        invalidTextDefault = 'Include ⇧, ⌥, Ctrl or ⌘';
    }

    let invalidMissingLetter = 'Include a letter';

    const downHandler = (keyEvent) => {
        let invalidMissingLetter = 'Include a letter';
        let keyValue;
        if (keyEvent.code.includes('Key')) {
            keyValue = keyEvent.code.replace('Key', '');
        } else {
            keyValue = keyEvent.key;
        }
        if (keyValue === 'Shift') {
            setShiftHeld(true);
            setInvalidText(invalidMissingLetter);
        }
        if (keyValue === 'Alt') {
            setAltHeld(true);
            setInvalidText(invalidMissingLetter);
        }
        if (keyValue === 'Meta') {
            setCommandHeld(true);
            setInvalidText(invalidMissingLetter);
        }
        if (keyValue === 'Control') {
            setControlHeld(true);
            setInvalidText(invalidMissingLetter);
        }
        if (isLetter(keyValue)) {
            setLetterHeld(keyValue);
            setInvalidText(invalidTextDefault);
        }
        if (keyValue === 'Escape') {
            turnOffKeyRecording();
        }
        keyEvent.stopPropagation();
        keyEvent.preventDefault();
    };

    const upHandler = (keyEvent) => {
        if (altHeld || shiftHeld || commandHeld || controlHeld) {
            setInvalidText(invalidMissingLetter);
        } else {
            setInvalidText(invalidTextDefault);
        }

        let keyValue;
        if (keyEvent.code.includes('Key')) {
            keyValue = keyEvent.code.replace('Key', '');
        } else {
            keyValue = keyEvent.key;
        }

        if (keyValue === 'Shift') {
            setShiftHeld(false);
        }
        if (keyValue === 'Alt') {
            setAltHeld(false);
        }
        if (keyValue === 'Meta') {
            setCommandHeld(false);
        }
        if (keyValue === 'Control') {
            setControlHeld(false);
        }
        if (isLetter(keyValue)) {
            setLetterHeld(null);
        }
    };

    if ((altHeld || shiftHeld || commandHeld || controlHeld) && letterHeld) {
        setInvalidText('');
        let hotkeyTextArray = [];
        if (altHeld) {
            hotkeyTextArray.push('alt');
        }
        if (shiftHeld) {
            hotkeyTextArray.push('shift');
        }
        if (commandHeld) {
            hotkeyTextArray.push('meta');
        }
        if (controlHeld) {
            hotkeyTextArray.push('ctrl');
        }
        hotkeyTextArray.push(letterHeld.toLowerCase());
        let localStorageString = hotkeyTextArray.join('+');

        setInputValue(formatLocalStorageValue(localStorageString, isUsingMac));
        setLocalStorage(localStorageString);
        turnOffKeyRecording();
    }

    return (
        <Box>
            <Flex>
                <Box
                    display="flex"
                    alignItems="center"
                    role="group"
                    cursor="pointer"
                    w="278px"
                    bg={bgColor[colorMode]}
                    pr="0px"
                    pl="16px"
                    ref={boxRef}
                    opacity={
                        indexSel !== index && indexSel !== null ? '0.6' : '1'
                    }
                    style={{
                        filter:
                            indexSel !== index && indexSel !== null
                                ? 'grayscale(0.75)'
                                : 'none'
                    }}
                    //borderWidth="2px"
                    borderRadius="10px"
                    //borderColor={collapsed ? 'black.300' : 'blue.300'}
                    boxShadow={collapsed ? 'none' : '0px 0px 0px 2px #4299E1'}
                    onClick={() => {
                        if (collapsed) {
                            recordShortcut();
                        } else {
                            turnOffKeyRecording();
                        }
                    }}
                >
                    <Tooltip
                        label={'Click to edit'}
                        zIndex="500000"
                        rounded="6px"
                        motionProps={tooltipAnimation}
                        placement={'top'}
                    >
                        <Flex my="auto" w="100%">
                            <Box
                                as="p"
                                my="auto"
                                mr="auto"
                                letterSpacing="0.42px"
                                fontSize="14px"
                                color={
                                    collapsed
                                        ? iconColor[colorMode]
                                        : subTitle[colorMode]
                                }
                                _groupHover={{
                                    color: collapsed
                                        ? hoverText[colorMode]
                                        : iconColor[colorMode]
                                }}
                            >
                                {collapsed
                                    ? inputValue
                                        ? inputValue
                                        : formatLocalStorageValue(
                                              defaultValue,
                                              isUsingMac
                                          )
                                    : 'Type shortcut'}
                            </Box>
                        </Flex>
                    </Tooltip>
                </Box>
                <Tooltip
                    label={'Reset to default'}
                    zIndex="500000"
                    rounded="6px"
                    motionProps={tooltipAnimation}
                    placement={'top'}
                >
                    <IconButton
                        my="auto"
                        mr="auto"
                        ml="12px"
                        rounded={'8px'}
                        padding="12px"
                        transition="0s"
                        //color={iconSmall[colorMode]}
                        icon={
                            <RefreshIcon color="var(--chakra-colors-iconMd)" />
                        }
                        onClick={() => {
                            setLocalStorage(defaultValue);
                            setInputValue(
                                formatLocalStorageValue(
                                    defaultValue,
                                    isUsingMac
                                )
                            );
                        }}
                        aria-label={''}
                    ></IconButton>
                </Tooltip>
            </Flex>
            {!collapsed && (
                <Text
                    mt="4px"
                    ml="auto"
                    letterSpacing="0.30px"
                    fontSize="12px"
                    color="red.600"
                >
                    {invalidText}
                </Text>
            )}
        </Box>
    );
};

export default ShortcutRecorder;
